import { Table, Badge, Menu, ActionIcon, Text, Paper, Stack, Avatar, Select, rem, Checkbox } from '@mantine/core';
import { IconDotsVertical, IconTrash, IconUser, IconShieldCheck } from '@tabler/icons-react';
import { formatDate } from '@/lib/utils';
import { useOrganization } from '@/hooks/useOrganization';
import { useCustomRoles, useAssignCustomRole } from '@/hooks/api/useCustomRoles';
import { useToggleAdminStatus } from '@/hooks/api/useOrganizationMembers';
import type { OrganizationMember } from '@/types/models';

interface UsersTableProps {
  members: OrganizationMember[];
  currentUserId: string;
  onRemove: (member: OrganizationMember) => void;
  onManagePermissions?: (member: OrganizationMember) => void;
  isLoading?: boolean;
}

const roleColors: Record<string, string> = {
  admin: 'red',
  manager: 'blue',
  member: 'gray',
};

const statusColors: Record<string, string> = {
  active: 'green',
  pending: 'yellow',
  inactive: 'gray',
};

export function UsersTable({ members, currentUserId, onRemove, onManagePermissions, isLoading }: UsersTableProps) {
  const { organizationId } = useOrganization();
  const { data: customRoles = [] } = useCustomRoles(organizationId || '');
  const assignCustomRole = useAssignCustomRole();
  const toggleAdminStatus = useToggleAdminStatus();

  const handleCustomRoleChange = (member: OrganizationMember, customRoleId: string | null) => {
    assignCustomRole.mutate({
      memberId: member.id,
      customRoleId: customRoleId === '' ? null : customRoleId,
    });
  };

  const handleAdminToggle = (member: OrganizationMember, isAdmin: boolean) => {
    toggleAdminStatus.mutate({
      memberId: member.id,
      isAdmin,
    });
  };

  const rows = members.map((member) => (
    <Table.Tr key={member.id}>
      <Table.Td>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Avatar color="blue" size={40} radius="xl">
            <IconUser size={20} />
          </Avatar>
          <div>
            <Text size="sm" fw={500}>
              {member.profile?.full_name || 'Unknown User'}
              {member.user_id === currentUserId && (
                <Badge ml="xs" size="xs" variant="light">
                  You
                </Badge>
              )}
            </Text>
            <Text size="xs" c="dimmed">
              {member.profile?.email}
            </Text>
          </div>
        </div>
      </Table.Td>
      <Table.Td>
        <Checkbox
          checked={member.is_admin}
          onChange={(e) => handleAdminToggle(member, e.currentTarget.checked)}
          disabled={member.user_id === currentUserId}
        />
      </Table.Td>
      <Table.Td>
        <Badge color={statusColors[member.status]} variant="light" size="sm">
          {member.status}
        </Badge>
      </Table.Td>
      <Table.Td>
        <Select
          placeholder={member.is_admin ? 'Admin - No role needed' : 'Select role'}
          value={member.custom_role_id || ''}
          onChange={(value) => handleCustomRoleChange(member, value)}
          data={[
            { value: '', label: 'None' },
            ...customRoles.map((role) => ({
              value: role.id,
              label: role.name,
            })),
          ]}
          size="xs"
          style={{ width: 180 }}
          disabled={member.user_id === currentUserId || member.is_admin}
        />
      </Table.Td>
      <Table.Td>{formatDate(member.joined_at)}</Table.Td>
      <Table.Td>
        <Menu shadow="md" width={200} position="bottom-end">
          <Menu.Target>
            <ActionIcon variant="subtle" color="gray" disabled={member.user_id === currentUserId}>
              <IconDotsVertical size={16} />
            </ActionIcon>
          </Menu.Target>

          <Menu.Dropdown>
            {onManagePermissions && member.is_admin && (
              <Menu.Item
                leftSection={<IconShieldCheck style={{ width: rem(14), height: rem(14) }} />}
                onClick={() => onManagePermissions(member)}
              >
                Manage Permissions
              </Menu.Item>
            )}
            <Menu.Item
              color="red"
              leftSection={<IconTrash style={{ width: rem(14), height: rem(14) }} />}
              onClick={() => onRemove(member)}
            >
              Remove from Organization
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <Stack gap="md">
      <Paper withBorder>
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>User</Table.Th>
              <Table.Th>Admin</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Custom Role</Table.Th>
              <Table.Th>Joined</Table.Th>
              <Table.Th>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {isLoading ? (
              <Table.Tr>
                <Table.Td colSpan={6}>
                  <Text ta="center" py="xl" c="dimmed">
                    Loading users...
                  </Text>
                </Table.Td>
              </Table.Tr>
            ) : rows.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={6}>
                  <Text ta="center" py="xl" c="dimmed">
                    No users found
                  </Text>
                </Table.Td>
              </Table.Tr>
            ) : (
              rows
            )}
          </Table.Tbody>
        </Table>
      </Paper>

      <Text size="sm" c="dimmed">
        Showing {members.length} user{members.length !== 1 ? 's' : ''}
      </Text>
    </Stack>
  );
}
